import { Button, Checkbox, Modal, Textarea } from "@mantine/core";
import { attempt, SmartwizError } from "@/utils/errors";
import "./ClientEmailModal.scss";
import ConfirmCloseModal from "@/components/ConfirmCloseModal/ConfirmCloseModal";
import { useEffect, useState } from "react";
import ConfirmSuccessContent from "../ConfirmSuccessContent/ConfirmSuccessContent";
import { sendEmailData } from "@/utils/api/email";
import { processEmailBody } from "@/utils/emailUtils";

interface ClientEmailModalProps {
  opened: boolean;
  onClose: () => void;
}

const ClientEmailModal = ({ opened, onClose }: ClientEmailModalProps) => {
  const personalInfo = localStorage.getItem("personalInfo");
  const personalInfoObj = JSON.parse(personalInfo ?? "{}");
  const email =
    personalInfoObj?.assignee?.email ?? personalInfoObj?.businessOwnerEmail;

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [message, setMessage] = useState("");

  const [confirmOpened, setConfirmOpened] = useState(false);
  const [successOpened, setSuccessOpened] = useState(false);

  const [isSending, setIsSending] = useState(false);

  const reset = () => {
    setSelectedOptions([]);
    setMessage("");
    setSuccessOpened(false);
  };

  const handleConfirmClose = () => {
    setConfirmOpened(false);
    onClose();
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    option: string
  ) => {
    if (e.target.checked) {
      setSelectedOptions([...selectedOptions, option]);
    } else {
      setSelectedOptions(selectedOptions.filter((o) => o !== option));
    }
  };

  const sendEmail = async (
    email: string,
    selectedOptions: string[],
    message: string
  ) => {
    setIsSending(true);

    await attempt(
      async () => {
        if (!email) {
          throw new SmartwizError({
            message: "No tax professional email found.",
          });
        }
        if (selectedOptions.length === 0) {
          throw new SmartwizError({
            message: "Please select at least one option for help needed.",
          });
        }
        if (!message.trim()) {
          throw new SmartwizError({
            message: "Email is missing a message.",
          });
        }

        const subject = `Client Support Request: ${selectedOptions.join(", ")}`;
        const success = await sendEmailData({
          to: [email],
          subject,
          body: processEmailBody(message),
          attachments: [],
        });

        if (success) {
          setSuccessOpened(true);
        }
        return success;
      },
      {
        successMessage: "Your email has been sent to the tax professional.",
        errorMessage:
          "An error occurred while sending the email. Please try again.",
      }
    );

    setIsSending(false);
  };

  // Reset success modal state when client email modal opens/closes
  useEffect(() => {
    if (opened) {
      reset();
    }
  }, [opened]);

  return (
    <Modal
      className="client-email-modal"
      title="Contact Tax Pro"
      opened={opened}
      onClose={onClose}
      centered
      size={650}
    >
      <div>
        {successOpened ? (
          <ConfirmSuccessContent onClose={onClose} />
        ) : (
          <div className="client-email-modal-container">
            <p>
              We’re here to help! Use the form below to send a message directly
              to your tax professional. They’ll receive your message via email{" "}
              {email ? <b>{email}</b> : ""} and will respond as soon as
              possible.
            </p>
            <div className="client-email-modal-checkbox-container">
              <h2>What do you need help with? Select all that apply.</h2>
              <span className="client-email-modal-checkbox-container-checkboxes">
                {["Client Intake Form", "Taxes or Tax Return", "Other"].map(
                  (option) => (
                    <Checkbox
                      key={option}
                      checked={selectedOptions.includes(option)}
                      onChange={(e) => handleCheckboxChange(e, option)}
                      color="#0077CC"
                      label={option}
                    />
                  )
                )}
              </span>
            </div>
            <div className="client-email-modal-textarea-container">
              <Textarea
                rows={6}
                label="Message"
                placeholder="Please describe the issue or concern you’re experiencing in detail."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <span className="client-email-modal-button-container">
              <Button
                onClick={() => setConfirmOpened(true)}
                size="md"
                variant="outline"
                color="#0077CC"
              >
                Cancel
              </Button>
              <Button
                loading={isSending}
                size="md"
                color="#0077CC"
                onClick={() => sendEmail(email, selectedOptions, message)}
                disabled={isSending}
              >
                Send message
              </Button>
            </span>
          </div>
        )}
      </div>
      <ConfirmCloseModal
        modalOpened={confirmOpened}
        closeModal={handleConfirmClose}
        handleSelectNo={() => setConfirmOpened(false)}
      />
    </Modal>
  );
};

export default ClientEmailModal;
